import { Alert, Box } from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  CertificateThemeRequest,
  ResponseOfCertificateTheme,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import CertificateThemeForm from "./CertificateThemeForm";

const CertificateThemeUpdateForm = () => {
  const { registerPromise } = useTrashablePromises();
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    certificateService,
    certificateThemesUrl,
    certificateThemeDetailsUrl,
  } = useGlobalContext();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const certificateThemeResult = useQuery<ResponseOfCertificateTheme>(
    ["certificateTheme", id],
    () => certificateService.getCertificateTheme(id!),
    {
      enabled: !!id,
    }
  );

  const certificateTheme = certificateThemeResult.data?.data;
  useTitle(
    certificateTheme?.name || t({ id: "certificateThemeFormFormUpdateButton" })
  );

  const onClickDetails = () => {
    navigate(certificateThemeDetailsUrl(id));
  };

  const onSubmit = async (values: CertificateThemeRequest) => {
    values.id = id;

    try {
      const result = await registerPromise(
        certificateService.saveCertificateTheme(values)
      );
      queryClient.invalidateQueries("certificateThemes");
      queryClient.invalidateQueries(["certificateTheme", id]);
      queryClient.invalidateQueries(["certificateThemePreview", id]);
      onClickDetails();

      return result;
    } catch (err: any) {
      return { [FORM_ERROR]: translateError(err, t) };
    }
  };

  if (!id) return <Navigate to={certificateThemesUrl()} replace />;
  if (certificateThemeResult.isLoading) return <Loader />;
  if (certificateThemeResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="certificateThemeLoadError" />
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <RedirectBackButton
        redirectRoute={certificateThemeDetailsUrl(id)}
        labelKey="backToDetailsButtonLabel"
      />
      <h2>
        <T id="certificateThemeFormEditTitle" />
      </h2>
      <CertificateThemeForm
        entity={certificateTheme}
        onSubmit={onSubmit}
        submitLabel={t({ id: "certificateThemeFormFormUpdateButton" })}
        onCancel={onClickDetails}
      />
    </>
  );
};

export default CertificateThemeUpdateForm;
